import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { fetchSessionRecording } from '../services/dispatchHelpApi'

/** Keep downloaded audio local to this player and this login, never in Redux or persistent storage. A
 *  `directUrl` (a broker call's recording, which the mobile plays the same way) plays as is, with no download. */
export function useRecordingPlayback(tabId: string, messageId: string, viewerId: string | null, directUrl?: string) {
    const audioRef = useRef<HTMLAudioElement>(null)
    const urlRef = useRef<string | null>(null)
    const requestRef = useRef<AbortController | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const audio = audioRef.current
        setLoading(false)
        setError(null)
        return () => {
            requestRef.current?.abort()
            requestRef.current = null
            audio?.pause()
            audio?.removeAttribute('src')
            audio?.load()
            if (urlRef.current?.startsWith('blob:')) URL.revokeObjectURL(urlRef.current)
            urlRef.current = null
        }
    }, [tabId, messageId, viewerId, directUrl])

    async function togglePlay() {
        const audio = audioRef.current
        if (!audio || !viewerId || requestRef.current) return
        if (!audio.paused) {
            audio.pause()
            return
        }
        setError(null)
        const controller = new AbortController()
        requestRef.current = controller
        try {
            if (!urlRef.current && directUrl) {
                urlRef.current = directUrl
                audio.src = directUrl
            } else if (!urlRef.current) {
                setLoading(true)
                const blob = await fetchSessionRecording(tabId, messageId, controller.signal)
                if (controller.signal.aborted) return
                urlRef.current = URL.createObjectURL(blob)
                audio.src = urlRef.current
            }
            await audio.play()
        } catch (err) {
            if (controller.signal.aborted) return
            // A failed element does not fetch again on a second `play()`; setting `src` again does, so forget it.
            if (directUrl) urlRef.current = null
            const status = isAxiosError(err) ? err.response?.status : undefined
            setError(
                status === 401 || status === 403
                    ? 'Your login no longer allows playback. Sign in with a Dispatch Help operator account.'
                    : status === 404
                      ? 'Recording is not available yet. Try again shortly.'
                      : err instanceof DOMException && err.name === 'NotAllowedError'
                        ? 'Recording loaded. Press Play again to listen.'
                        : 'Could not play the recording. Press Play to retry.'
            )
        } finally {
            if (!controller.signal.aborted) {
                requestRef.current = null
                setLoading(false)
            }
        }
    }

    return { audioRef, loading, error, togglePlay }
}
